import { Divider } from "@mui/material";
import {
  DatabaseIcon,
  GlobeIcon,
  NavigationArrowIcon,
  LockIcon,
  LockOpenIcon,
  BugIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";

import { Menu, MenuItem } from "@/components/Menu";
import { vConsole } from "@/debug/vConsole";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { type Locale } from "@/utils/locale";
import { devLocaleStorage } from "@/utils/localStorage";

import type { ActivePanel } from "./DevToolsHub";
import type { UsePurchaseBlockReturn } from "./tools/usePurchaseBlock";

interface DevToolsMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  activePanel: ActivePanel;
  onTogglePanel: (panel: NonNullable<ActivePanel>) => void;
  purchaseBlock: UsePurchaseBlockReturn;
}

export function DevToolsMenu({
  anchorEl,
  onClose,
  activePanel,
  onTogglePanel,
  purchaseBlock: {
    isPurchaseAllowed,
    purchaseAllowedSecondsLeft,
    togglePurchaseBlock,
  },
}: DevToolsMenuProps) {
  const { locale } = useLocaleInfo();
  const nextLocale: Locale = locale === "ar" ? "en" : "ar";

  return (
    <Menu
      id="dev-tools-menu"
      triggerId="dev-tools-trigger"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      dir="ltr"
      disableScrollLock
      position="top-end"
    >
      {!!vConsole && (
        <MenuItem
          selected={activePanel === "console"}
          startIcon={<BugIcon size={20} />}
          onClick={() => {
            onTogglePanel("console");
            onClose();
          }}
          label="Console"
        />
      )}

      <MenuItem
        selected={activePanel === "rq"}
        startIcon={<DatabaseIcon size={20} />}
        onClick={() => {
          onTogglePanel("rq");
          onClose();
        }}
        label="React Query"
      />

      <MenuItem
        selected={activePanel === "router"}
        startIcon={<NavigationArrowIcon size={20} />}
        onClick={() => {
          onTogglePanel("router");
          onClose();
        }}
        label="Router"
      />

      <Divider />

      <MenuItem
        startIcon={<GlobeIcon size={20} />}
        secondaryLabel={`${locale.toUpperCase()} → ${nextLocale.toUpperCase()}`}
        onClick={() => {
          devLocaleStorage.set(nextLocale);
          onClose();
          window.location.reload();
        }}
        label="Language"
      />

      <MenuItem
        startIcon={
          isPurchaseAllowed ? (
            <LockOpenIcon size={20} className="text-semantic-error-strong" />
          ) : (
            <LockIcon size={20} />
          )
        }
        secondaryLabel={
          isPurchaseAllowed
            ? `Disables in ${dayjs.duration(purchaseAllowedSecondsLeft, "seconds").format("mm:ss")}`
            : "Tap to unlock"
        }
        onClick={() => {
          onClose();
          togglePurchaseBlock();
        }}
        label={
          <span
            className={isPurchaseAllowed ? "text-semantic-error-strong" : ""}
          >
            {isPurchaseAllowed ? "Purchases allowed" : "Purchases blocked"}
          </span>
        }
      />
    </Menu>
  );
}
